import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

import chipImg from "../../images/about/chip-main.jpg";
import waferImg from "../../images/about/wafer.jpg";
import testingImg from "../../images/about/testing.jpg";
import labImg from "../../images/about/lab.jpg";

import downArrow from "../../images/icon/down-white-icon.svg";

import "./WhoWeAre.css";

gsap.registerPlugin(ScrollToPlugin);

const WhoWeAre: React.FC = () => {
  useEffect(() => {
    const section = document.getElementById("who-we-are");
    if (!section || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const context = gsap.context(() => {}, section);
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const group = entry.target;
        context.add(() => {
          gsap.from(group.children, {
            autoAlpha: 0,
            y: 32,
            duration: 0.85,
            stagger: 0.13,
            ease: "power2.out",
            clearProps: "opacity,visibility,transform",
          });
        });
        observer.unobserve(group);
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -40px 0px" });

    section.querySelectorAll(".who-we-are-content, .who-we-are-visual").forEach((group) => observer.observe(group));
    return () => {
      observer.disconnect();
      context.revert();
    };
  }, []);

  useEffect(() => {
    const scrollLinks = document.querySelectorAll<HTMLAnchorElement>(
      ".who-we-are-section a.scrollspy-btn[href^='#']"
    );

    const handleScroll = (e: Event) => {
      e.preventDefault();

      const link = e.currentTarget as HTMLAnchorElement;
      const targetId = link.getAttribute("href");

      if (!targetId) return;

      const target = document.querySelector(targetId);

      if (target) {
        gsap.to(window, {
          duration: 1.2,
          scrollTo: {
            y: target,
            offsetY: 80,
          },
          ease: "power2.out",
        });
      }
    };

    scrollLinks.forEach((link) => {
      link.addEventListener("click", handleScroll);
    });

    return () => {
      scrollLinks.forEach((link) => {
        link.removeEventListener("click", handleScroll);
      });
    };
  }, []);

  return (
    <section
      className="about o-clip pos-rel who-we-are-section"
      id="who-we-are"
    >
      <div className="container-fluid">
        <div className="who-we-are-wrap">
          <div className="who-we-are-grid" />

          <div className="who-we-are-inner">
            {/* LEFT CONTENT */}
            <div className="who-we-are-content">
              <div className="who-we-are-sub-title">
                <span className="who-we-are-sub-square" />
                WHO WE ARE
              </div>

              <div className="who-we-are-sub-line" />

              <h2 className="who-we-are-title">
                Engineering Excellence for the
                <span>Semiconductor Industry</span>
              </h2>

              <p className="who-we-are-description">
                Digicomm Semiconductor is an engineering services company
                specializing in VLSI design and semiconductor product
                development. We support semiconductor organizations across the
                silicon development lifecycle with expertise in design,
                verification, physical design, design-for-test, and silicon
                realization. By combining technical excellence with disciplined
                execution, we help transform complex engineering challenges
                into reliable silicon outcomes.
              </p>

              <Link className="who-we-are-btn" to="/about">
                <span className="who-we-are-btn-arrow">
                  <svg
                    width="16"
                    height="28"
                    viewBox="0 0 16 28"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M2 2L14 14L2 26"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>

                <span className="who-we-are-btn-text">
                  Learn More
                </span>
              </Link>
            </div>

            {/* RIGHT VISUAL */}
            <div className="who-we-are-visual">
              <div className="who-main-chip">
                <img
                  src={chipImg}
                  alt="Semiconductor chip"
                />
              </div>

              <div className="who-floating-img who-wafer-img">
                <img
                  src={waferImg}
                  alt="Semiconductor wafer"
                />
              </div>

              <div className="who-floating-img who-testing-img">
                <img
                  src={testingImg}
                  alt="Semiconductor testing"
                />
              </div>

              <div className="who-floating-img who-lab-img">
                <img
                  src={labImg}
                  alt="Semiconductor laboratory"
                />
              </div>

              <div className="who-decor-dots who-decor-one">
                <span />
                <span />
                <span />
              </div>

              <div className="who-decor-dots who-decor-two">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>

          <a
            href="#service"
            className="who-scroll-down scrollspy-btn"
          >
            <img
              src={downArrow}
              alt=""
              aria-hidden="true"
            />
            <span>Scroll Down</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default WhoWeAre;